import {LOCAL_STORAGE_KEYS, WIDGET_TYPES} from "../consts";
import {AppStore} from './app-store';
import {AppSettings} from "./models/app-settings";
import {HistoryManager} from "../managers/history-manager";
import {TermsUtils} from "../../infrastructure/utils/terms-utils";
import {RouterController} from "../components/router/router-controller";
import {EntityDisplayEvent} from "../../infrastructure/models/analytics/entity-display-event";
import {BaseLinkModel} from "../../infrastructure/models/link/base-link-model";
import {ModuleController} from "../../infrastructure/controllers/module-controller";
import {APP, DEFAULT_CULTURE_NAME, ENTITY_TYPES} from "./consts";
import {DatetimeUtils} from "../../infrastructure/utils/datetime-utils";
import {LanguageDomainModel} from "../../domain/locale/models/language/language.domain-model";
import {AppModuleState} from "./models/app-module.state";
import {ExitEvent} from "../../infrastructure/models/analytics/exit-event";
import {BaseConfig} from "../../infrastructure/config/base-config";
import {BaseMobxStore} from "../../infrastructure/mobx/base-mobx-store";
import {HandlerList} from "../../infrastructure/handlers/handler-list";
import {IOC} from "../../infrastructure/ioc";
import {SiteConfig} from "../bootstrap/models/site-config";
import {DomUtils} from "../../infrastructure/utils/dom-utils";
import {LoggerUtils} from "../../infrastructure/utils/logger/logger-utils";
import {NavigationManager} from "../managers/navigation-manager";
import {LocalStorageUtils} from "../../infrastructure/utils/local-storage.utils";
import {LocaleDomainService} from "../../domain/locale/services/locale/locale.domain-service";
import {AnalyticsService} from "../../third-party-services/analytics/analytics-service";
import {DeviceStore} from "./device.store";
import {LocaleDomainModel} from "../../domain/locale/models/locale/locale.domain-model";
import {BaseManagedService} from "../../infrastructure/domain-services/base-managed-service";
import {AppHandlers} from "./handlers/app-handlers";
import {BootstrapSettings} from "../bootstrap/models/bootstrap-settings";
import {DashboardLinkModel} from "../../containers/dashboard-container/models/dashboard-container-link-model";
import {GameCenterLinkModel} from "../../containers/game-center-container/models/game-center.link-model";
import {SliderLinkModel} from "../../containers/slider-container/models/slider-link-model";
import {TopGamesLinkModel} from "../../widgets/top-games-widget/models/top-games-link-model";
import {PredictionsLinkModel} from "../../containers/predictions-container/models/predictions-link-model";
import {AppModuleConfig} from "./app-config";
import {StandingsPreviewLinkModel} from "../../containers/standings-preview-container/models/standings-preview-link-model";
import {BettingOpportunityLinkModel} from "../../containers/betting-opportunity-container/models/betting-opportunity-link-model";
import {EntityStatsLinkModel} from "../../containers/entity-stats-container/models/entity-stats-link-model";
import {registerRoutesSchema} from "../../containers/routes-containers-registry";
import {APP_REMOTE_CONFIG} from "./app-remote-config";
import {RemoteConfigManager} from "../../third-party-services/remote-config/remote-config-manager";
import {action, runInAction} from "mobx"
import type {RouteModel} from "../components/router/models/route-model";
import {SessionStorageUtils} from "../../infrastructure/utils/session-storage.utils";
import {OutrightsLinkModel} from "../../containers/outrights-container/models/outrights-link-model";
import {KeyPlayersLinkModel} from "../../containers/kp-container/models/key-players-link-model";
import {WEBSITE_MODULE_REMOTE_CONFIG} from "../../apps/website/website-module.remote-config";
import {EntityTrendsLinkModel} from "../../containers/entity-trends-container/models/entity-trends-link-model";
import {pagesContainersRegistry} from "../../containers/pages-containers-registry";
import {BetBoostsLinkModel} from "../../containers/bet-boosts-container/models/bet-boosts-link-model";
import {TodayPredictionsLinkModel} from "../../containers/today-predictions-container/models/today-predictions-link-model";
import {CalculatorLinkModel} from "../../containers/calculator-container/models/calculator-link-model";
import {AccumulatorLinkModel} from "../../containers/accumulator-container/models/accumulator-link-model";
import {BestOddsLinkModel} from "../../containers/best-odds-container/models/best-odds-link-model";
import {CorrectScoreLinkModel} from "../../containers/correct-score-container/models/correct-score.link-model";
import {PreGameStatsLinkModel} from "../../containers/pre-game-stats-container/models/pre-game-stats-link-model";
import {OddsComparisonLinkModel} from "../../containers/odds-comparison-container/models/odds-comparison-link-model";
import {CompetitorStandingsPageLinkModel} from "../../containers/competitor-container/pages/standings-page/models/competitor.standings-page.link-model";
import {EntityHistoryLinkModel} from "../../containers/entity-history-container/models/entity-history-link-model";
import {SquadLinkModel} from "../../containers/squad-container/models/squad-link-model";

export class AppController extends ModuleController {
  entityDisplay: EntityDisplayEvent;
  store: AppStore;
  state: AppModuleState;
  ioc: IOC;
  routesConfig: Array;
  pages: Map<String, RouteModel>;
  localeDomainService: LocaleDomainService;

  static injectables = [...BaseManagedService.injectables, AppModuleConfig, AppStore, AppHandlers, LocaleDomainService, AnalyticsService, RouterController, RemoteConfigManager];

  constructor(domUtils: DomUtils, logger: LoggerUtils, siteConfig: SiteConfig, deviceStore: DeviceStore, appStore: AppStore, navigationManager: NavigationManager, historyManager: HistoryManager, localStorageUtils: LocalStorageUtils, config: BaseConfig, store: BaseMobxStore, handlers: HandlerList, localeDomainService: LocaleDomainService, analyticsService: AnalyticsService, routerController: RouterController, remoteConfigManager: RemoteConfigManager) {
    super(domUtils, logger, siteConfig, deviceStore, appStore, navigationManager, historyManager, localStorageUtils, config, store, handlers, analyticsService, routerController);

    this.localeDomainService = localeDomainService;
    this.analyticsService = analyticsService;
    this.routerController = routerController;
    this.remoteConfigManager = remoteConfigManager;
  }

  get termsUtils(): TermsUtils {
    return this.ioc.getInstance(TermsUtils);
  }

  get datetimeUtils(): DatetimeUtils {
    return this.ioc.getInstance(DatetimeUtils);
  }

  get sessionStorageUtils(): SessionStorageUtils {
    return this.ioc.getInstance(SessionStorageUtils);
  }

  init(props) {
    this.state.settings = new AppSettings(props.params, this.siteConfig);

    super.init(props);

    this.ioc = props.ioc;
    this.ioc.replaceInstance(AppSettings, this.store.state.settings);

    this.fetch().then(() => this.action(APP.ACTION_TYPES.publishUserData));

    this.state.basename = this.store.state.settings.isWidget ? this.ioc.getInstance(HistoryManager).currentPath : '';


    this.historyManager.registerToRouteChanged((x) => this.onUrlChange(x));
    this.historyManager.registerToRouteChanged((x) => this.toggleBackStripeVisibility(x));
    this.navigationManager.registerToCurrentLinkModelChange((model: BaseLinkModel) => this.handleLinkModelChange(model));
    setTimeout(() => this.analyticsService.init(), 2000);

    this.shouldValidatePoweredBy && this.action(APP.ACTION_TYPES.poweredByDetector);
  }

  async onLoad(type = this.type, name = this.name) {
    const promise = super.onLoad(type, name);

    if (this.localStorageUtils.get(LOCAL_STORAGE_KEYS.showTagSnippetCoachMark) === undefined) {
      this.localStorageUtils.set(LOCAL_STORAGE_KEYS.showTagSnippetCoachMark, true);
    }

    if (this.localStorageUtils.get(LOCAL_STORAGE_KEYS.enableNotifications) && (!this.localStorageUtils.get(LOCAL_STORAGE_KEYS.forceUpdateUser) || this.localStorageUtils.get(LOCAL_STORAGE_KEYS.forceUpdateUser) < 1)) {
      this.action(APP.ACTION_TYPES.fetchUsers);
      this.localStorageUtils.set(LOCAL_STORAGE_KEYS.forceUpdateUser, 1)
    }

    this.domUtils.onAppPageHide((event: PageTransitionEvent) => {
      if (event.persisted) {
        return;
      }

      this.historyManager.clearState();
      this.localStorageUtils.set(LOCAL_STORAGE_KEYS.isReturnedUser, true);
      this.analyticsService.createEvent(new ExitEvent(this.state.isUserHoverAd));
      this.analyticsService.flush()
    });

    return promise;
  }

  async onUrlChange(path: Object) {
     const possibleCultureDisplayName = path.split('/')[1] || DEFAULT_CULTURE_NAME;
    const isValidCulture = !!this.localeDomainService.languages.find((x: LanguageDomainModel) => x.displayName === possibleCultureDisplayName);
    const shouldRestoreToDefaultCulture = !isValidCulture && this.localeDomainService.locale.language.cultureName !== DEFAULT_CULTURE_NAME;
    const hasCultureChanged = isValidCulture && (possibleCultureDisplayName !== this.localeDomainService.locale.language.displayName);

    if (hasCultureChanged || shouldRestoreToDefaultCulture) {

      if (this.store.state.appNotificationDisplayModel.allowNotifications) {
        this.action(APP.ACTION_TYPES.fetchUsers);
      }
      this.store.setDataInitialized(false);
      await this.fetch();

      this.action(APP.ACTION_TYPES.clearStates);
      window.initPlaceholderDirection?.();
    }
  }

  async fetch() {
    const cultureName = await this.resolveCulture(this.store.state.settings);
    const locale: LocaleDomainModel = await this.initLocale(cultureName);
    await this.action(APP.ACTION_TYPES.fetchTerms);
    await this.action(APP.ACTION_TYPES.fetchRaffleBookmaker);

    await this.action(APP.ACTION_TYPES.fetchEntitiesRecognizer);
    this.initialRouter(locale);
    this.getInitialData();

    this.handlers.items.forEach(handler => handler.onLoadCompleted());
  }

  initialRouter(locale) {
    this.routesConfig = registerRoutesSchema(this.ioc);
    this.pages = pagesContainersRegistry(this.ioc)
    const initialLinkModel = this.getInitialLinkModel(this.state.settings.bootstrapSettings);

    this.routerController.initializeRoutes(this.routesConfig, locale.language.displayName, initialLinkModel, this.pages);

    if (initialLinkModel) {
      const routeModel: RouteModel = this.routerController.findRouteByName(initialLinkModel?.name);

      runInAction(() => {
        this.state.initialRouteModel = routeModel;
        this.state.initialLinkModel = initialLinkModel;
      })
    }

    this.store.setDataInitialized(true);
  }

  getInitialData(): * {
    this.state.importantInfoStripDisplay = this.config.getValue(APP_REMOTE_CONFIG.IMPORTANT_INFORMATION_STRIP_DISPLAY) || !!this.localStorageUtils.get(LOCAL_STORAGE_KEYS.importantInformationStripDisplay);
    this.state.appBolaoDisplayModel.isNavigationsVisible = this.config.getValue(APP_REMOTE_CONFIG.BOLAO_SHOW_NAVIGATIONS) || !!this.localStorageUtils.get(LOCAL_STORAGE_KEYS.showBolaoNavigations);
    this.state.appBolaoDisplayModel.isVisible = this.config.getValue(APP_REMOTE_CONFIG.BOLAO_SHOW_GAME) || !!this.localStorageUtils.get(LOCAL_STORAGE_KEYS.showBolao);
    this.state.appBolaoDisplayModel.isFloatingVisible = this.config.getValue(WEBSITE_MODULE_REMOTE_CONFIG.BOLAO_SHOW_PROMOTION) || !!this.localStorageUtils.get(LOCAL_STORAGE_KEYS.showBolaoFloating);

    this.remoteConfigManager.registerToInitCallback(() => runInAction(() => {
      this.state.importantInfoStripDisplay = this.config.getValue(APP_REMOTE_CONFIG.IMPORTANT_INFORMATION_STRIP_DISPLAY) || !!this.localStorageUtils.get(LOCAL_STORAGE_KEYS.importantInformationStripDisplay);
      this.state.appBolaoDisplayModel.isNavigationsVisible = this.config.getValue(APP_REMOTE_CONFIG.BOLAO_SHOW_NAVIGATIONS) || !!this.localStorageUtils.get(LOCAL_STORAGE_KEYS.showBolaoNavigations);
      this.state.appBolaoDisplayModel.isVisible = this.config.getValue(APP_REMOTE_CONFIG.BOLAO_SHOW_GAME) || !!this.localStorageUtils.get(LOCAL_STORAGE_KEYS.showBolao);
      this.state.appBolaoDisplayModel.isFloatingVisible = this.config.getValue(WEBSITE_MODULE_REMOTE_CONFIG.BOLAO_SHOW_PROMOTION) || !!this.localStorageUtils.get(LOCAL_STORAGE_KEYS.showBolaoFloating);
    }))

    this.action(APP.ACTION_TYPES.fetchSports);
    this.action(APP.ACTION_TYPES.wizardABTesting);
    this.action(APP.ACTION_TYPES.fetchOdds);
    this.action(APP.ACTION_TYPES.broadcastUserData);
  }

  async initLocale(cultureName: String) {
    this.initDateFormats(cultureName);

    return this.action(APP.ACTION_TYPES.fetchLocale, {cultureName}).then((locale: LocaleDomainModel) => {
      this.localStorageUtils.set(LOCAL_STORAGE_KEYS.langId, locale.language.id)

      runInAction(() => {
        this.setDirection(locale);
        this.store.setLocale(locale);
      })

      return locale;
    });
  }

  @action gotoInitialRoute(routeModel: RouteModel, initialLinkModel: BaseLinkModel) {
    this.navigationManager.gotoRoute(routeModel, initialLinkModel, true);
  }

  toggleBackStripeVisibility() {
    this.state.isBackStripeVisible = this.historyManager.hasBack;
  }

  async resolveCulture({cultureName, widgetType}: AppSettings): Promise<String> {
    if (!cultureName) {
      cultureName = this.localStorageUtils.get(LOCAL_STORAGE_KEYS.cultureName);
    }

    const isWidget = widgetType !== WIDGET_TYPES.website;
    const shouldSetDefaultCultureName = !cultureName && isWidget;
    const shouldResolveCultureFromUrl = !cultureName && !isWidget;

    if (shouldSetDefaultCultureName) {
      cultureName = DEFAULT_CULTURE_NAME;
    }

    if (shouldResolveCultureFromUrl) {
      const urlCulture = window.location.pathname.split('/')[1] || DEFAULT_CULTURE_NAME;
      const language = this.localeDomainService.findLangByDisplayName(urlCulture);

      cultureName = language ? language.cultureName.toLowerCase() : DEFAULT_CULTURE_NAME.toLowerCase();
    }

    return cultureName;
  }

  initDateFormats(cultureName = DEFAULT_CULTURE_NAME) {
    this.datetimeUtils.initialize(cultureName);
  }

  @action setDirection(locale) {
    if (locale) {
      this.domUtils.setDirection(locale.language.direction);
      this.device.direction = locale.language.direction;
    }
  }

  resolve(type) {
    return this.ioc.getInstance(type);
  }

  getInitialLinkModel(bootstrapSettings: BootstrapSettings): BaseLinkModel {
    const entityType: ENTITY_TYPES = bootstrapSettings.entityType || this.state.entitiesRecognizerDataSource?.entityType;
    const entityId: Number = bootstrapSettings.entityId || this.state.entitiesRecognizerDataSource?.entityId;

    const entitiesWidgetTypes = {
      [WIDGET_TYPES.entityScores]: {
        [ENTITY_TYPES.competition]: WIDGET_TYPES.competitionScores, [ENTITY_TYPES.team]: WIDGET_TYPES.competitorScores
      }, [WIDGET_TYPES.entityStandings]: {
        [ENTITY_TYPES.competition]: WIDGET_TYPES.competitionStandings,
        [ENTITY_TYPES.team]: WIDGET_TYPES.competitorStandings
      },
    }

    const isEntityWidget = [WIDGET_TYPES.entityScores, WIDGET_TYPES.entityStandings].includes(bootstrapSettings.widgetType);

    if (isEntityWidget) {
      bootstrapSettings.widgetType = entitiesWidgetTypes[bootstrapSettings.widgetType][entityType];
    }

    const widgetTypesUrls = {
      [WIDGET_TYPES.allScores]: new DashboardLinkModel('', ''), // [WIDGET_TYPES.newAllScores]: new AllScoresLinkModel('', sportName),
      [WIDGET_TYPES.game]: new GameCenterLinkModel(entityId, 'x', 'y', 'competitors'),
      [WIDGET_TYPES.slider]: new SliderLinkModel(entityType, entityId),
      [WIDGET_TYPES.topGames]: new TopGamesLinkModel(entityType, entityId),
      [WIDGET_TYPES.competitorStandings]: new CompetitorStandingsPageLinkModel(entityId, 'x', 'y'),
      [WIDGET_TYPES.predictions]: new PredictionsLinkModel(entityType, entityId),
      [WIDGET_TYPES.standingsPreview]: new StandingsPreviewLinkModel(entityType, entityId),
      [WIDGET_TYPES.bettingOpportunity]: new BettingOpportunityLinkModel(entityType, entityId),
      [WIDGET_TYPES.keyPlayers]: new KeyPlayersLinkModel(bootstrapSettings.entityType, bootstrapSettings.entityId),
      [WIDGET_TYPES.entityStats]: new EntityStatsLinkModel(bootstrapSettings.entityType, bootstrapSettings.entityId),
      [WIDGET_TYPES.squad]: new SquadLinkModel(bootstrapSettings.entityType, bootstrapSettings.entityId),
      [WIDGET_TYPES.trends]: new EntityTrendsLinkModel(entityType, entityId),
      [WIDGET_TYPES.outrights]: new OutrightsLinkModel(bootstrapSettings.entityType, bootstrapSettings.entityId),
      [WIDGET_TYPES.betBoosts]: new BetBoostsLinkModel(),
      [WIDGET_TYPES.todayPredictions]: new TodayPredictionsLinkModel(entityType, entityId),
      [WIDGET_TYPES.bestOdds]: new BestOddsLinkModel(entityType, entityId),
      [WIDGET_TYPES.calculators]: new CalculatorLinkModel(),
      [WIDGET_TYPES.accumulator]: new AccumulatorLinkModel(),
      [WIDGET_TYPES.correctScore]: new CorrectScoreLinkModel(bootstrapSettings.entityId),
      [WIDGET_TYPES.preGameStats]: new PreGameStatsLinkModel(),
      [WIDGET_TYPES.oddsComparison]: new OddsComparisonLinkModel(bootstrapSettings.entityType, bootstrapSettings.entityId),
      [WIDGET_TYPES.entityHistory]: new EntityHistoryLinkModel(bootstrapSettings.entityType, bootstrapSettings.entityId)
    }

    const linkModel: BaseLinkModel = widgetTypesUrls[bootstrapSettings.widgetType];

    return linkModel;
  }

  handleLinkModelChange(model: BaseLinkModel) {
    runInAction(() => {
      this.state.currentLinkModel = model;
    })
  }

  get shouldValidatePoweredBy(): Boolean {
    const hidePoweredBy: Boolean = this.state.settings.bootstrapSettings.hidePoweredBy;
    const validPublisherId: Boolean = this.state.settings.bootstrapSettings.publisherId > 0;
    const shouldValidatePoweredBy: Boolean = this.state.settings.isWidget && !validPublisherId && !hidePoweredBy;

    return shouldValidatePoweredBy
  }
}
